import { CellContext, Column, ColumnDef, FilterFn } from '@tanstack/react-table';
import { DataTableColumnHeader } from '@/components/Common/DataTable';
import { MappedStaffUnitDetails } from '#server/models/school-staff-unit';
import { ActionsDropDown } from '@/components/Common';
import { Ref } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@curtin-dxp/curtin-ui';
import { useNavigate } from 'react-router';

export interface StaffUnitColumnsProps {
  actionDropdownRowId: string | null;
  setActionDropdownRowId: (id: string | null) => void;
  setSelectedCampusId: (campusId: number) => void;
  setLastUwId: (campusId: number) => void;
}

export interface ExtendedMappedStaffUnitDetails extends MappedStaffUnitDetails {
  parentRef: Ref<HTMLDivElement>;
  deleteCallback: CallableFunction;
}

const createHeader = <TData,>(title: string, sortable: boolean) => {
  return ({ column }: { column: Column<TData, unknown> }) => (
    <DataTableColumnHeader
      column={column}
      title={title}
      sortable={sortable}
      titleClassName="text-b2 text-secondary-grey-02 font-semibold"
    />
  );
};

const createCell =
  (
    key: string,
    actionDropdownRowId?: string | null,
    setActionDropdownRowId?: (id: string | null) => void,
    setSelectedCampusId?: (campusId: number) => void,
    setLastUwId?: (campusId: number) => void,
    className?: string,
  ) =>
  (cellContext: CellContext<ExtendedMappedStaffUnitDetails, unknown>) => {
    const { row, cell } = cellContext;
    const value = row.getValue(key);
    const navigate = useNavigate();
    const handleRedirect = (campusId: number, uwId: number, url: string) => {
      if (setSelectedCampusId && setLastUwId) {
        setSelectedCampusId(campusId);
        setLastUwId(uwId);
      }
      navigate(url);
    };

    if (key == 'unit') {
      return (
        <div className={cn('flex h-[100px] w-[274px] items-center gap-3 px-1 py-3 pr-[12px]', className)}>
          <div className="flex w-[218px] flex-col items-start justify-center gap-2">
            <div className="flex text-b1 font-semibold">{row.original['udc']}</div>
            <div className="flex text-b3">{row.original['unitName']}</div>
          </div>
          <ActionsDropDown
            className="ml-0 mr-2 pl-0"
            key={`${row.original['udc']}-action`}
            // @ts-expect-error Some funky tan-stack column type shii here, just ignore it ;)
            parentRef={cell.column.columnDef.parentRef}
            options={[
              <Button
                onClick={() =>
                  handleRedirect(
                    row.original['campusId'],
                    row.original['unitWorkloadId'],
                    `/unit-workloads/${row.original['unitWorkloadId']}`,
                  )
                }
                className="w-full justify-start border-none p-0 py-[10px] pl-2"
                variant={null}
              >
                Manage unit allocations
              </Button>,
              <Button
                onClick={() =>
                  handleRedirect(
                    row.original['campusId'],
                    row.original['unitWorkloadId'],
                    `/unit-workloads/${row.original['unitWorkloadId']}/teaching-delivery`,
                  )
                }
                className="w-full justify-start border-none p-0 py-[10px] pl-2"
                variant={null}
              >
                TD
              </Button>,
              <Button
                onClick={() =>
                  handleRedirect(
                    row.original['campusId'],
                    row.original['unitWorkloadId'],
                    `/unit-workloads/${row.original['unitWorkloadId']}/teaching-related-duties`,
                  )
                }
                className="w-full justify-start border-none p-0 py-[10px] pl-2"
                variant={null}
              >
                TRD
              </Button>,
              <Button
                onClick={() => {
                  // Had to do this for some reason to properly type this,
                  // because it's nested in the onClick
                  const cellColumnDefinition = { ...cell.column.columnDef };
                  if ('deleteCallback' in cellColumnDefinition) {
                    const callback = cellColumnDefinition.deleteCallback as CallableFunction;
                    callback({
                      unitWorkloadId: cell.row.original.unitWorkloadId,
                      academicId: cell.row.original.staffId,
                    });
                  }
                }}
                className="w-full justify-start border-none p-0 py-[10px] pl-2"
                variant={null}
              >
                Remove unit
              </Button>,
            ]}
            actionDropdownRowId={actionDropdownRowId}
            setActionDropdownRowId={setActionDropdownRowId}
            rowId={`staff-${cell.row.id}`}
          />
        </div>
      );
    }
    if (key == 'campus') {
      return (
        <div className="flex w-[180px] flex-col items-start justify-center gap-2">
          <div className="flex text-b3">{row.original['campusName']}</div>
          <div className="flex text-b3">{row.original['campusStudyMode']}</div>
        </div>
      );
    }
    return (
      <div className={cn('flex h-[100px] w-[162px] items-center px-1 py-3 text-b3', className)}>
        <>{value}</>
      </div>
    );
  };

// columnId is needed in filterFn column option (https://tanstack.com/table/v8/docs/guide/column-filtering#filterfns)
const unitFilterFn: FilterFn<ExtendedMappedStaffUnitDetails> = (row, columnId, value) => {
  const unit = `${row.original.udc} ${row.original.unitName}`.toLowerCase();
  return unit.includes(value.toLowerCase());
};

export const staffUnitColumns = ({
  actionDropdownRowId,
  setActionDropdownRowId,
  setSelectedCampusId,
  setLastUwId,
}: StaffUnitColumnsProps): ColumnDef<ExtendedMappedStaffUnitDetails>[] => {
  return [
    {
      id: 'unit',
      accessorFn: (row) => `${row.udc} ${row.unitName}`,
      filterFn: unitFilterFn,
      sortingFn: 'text',
      header: createHeader('Unit', true),
      cell: createCell('unit', actionDropdownRowId, setActionDropdownRowId, setSelectedCampusId, setLastUwId),
    },
    {
      accessorKey: 'campus',
      accessorFn: (row) => `${row.campusName} ${row.campusStudyMode}`,
      header: createHeader('Campus', false),
      cell: createCell('campus'),
    },
    {
      accessorKey: 'studyPeriodName',
      header: createHeader('Study period', false),
      cell: createCell('studyPeriodName'),
    },
    {
      accessorKey: 'tdHours',
      header: createHeader('TD', false),
      cell: createCell('tdHours', undefined, undefined, undefined, undefined, 'w-[100px]'),
    },
    {
      accessorKey: 'trdHours',
      header: createHeader('TRD', false),
      cell: createCell('trdHours', undefined, undefined, undefined, undefined, 'w-[100px]'),
    },
    {
      accessorKey: 'schoolName',
      header: createHeader('School', false),
      cell: createCell('schoolName', undefined, undefined, undefined, undefined, 'w-[280px]'),
    },
  ];
};
